import { Box, Checkbox, CheckboxProps, useTheme } from "@mui/material";
import { ReactElement, forwardRef, ForwardedRef } from "react";
import { CheckboxIcon, CheckmarkIcon, MinusIcon } from "src/foundations/icons";
import { SenecFPPTheme } from "src/styles/fpp-theme/theme.types";

export interface SenecCheckboxProps extends CheckboxProps {
    "data-testid"?: string;
    hasError?: boolean;
}

export const SenecCheckbox = forwardRef(
    (
        { "data-testid": dataTestId = "checkbox", hasError, ...rest }: SenecCheckboxProps,
        ref: ForwardedRef<HTMLButtonElement>,
    ): ReactElement => {
        const theme: SenecFPPTheme = useTheme();

        const BaseIcon = ({ color, disabledColor }: { color?: string; disabledColor?: string }) => {
            return (
                <CheckboxIcon
                    sx={{
                        width: "20px",
                        height: "20px",
                        fill: color ?? theme.palette.neutralGray000.main,
                        "input:hover ~ &": {
                            fill: theme.palette.neutralGray00.main,
                        },
                        "input:disabled ~ &": {
                            opacity: 0.5,
                        },
                        ".Mui-focusVisible &": {
                            outline: `2px auto`,
                            outlineOffset: 2,
                            borderRadius: 1,
                        },
                        ".Mui-disabled &": {
                            fill: disabledColor,
                            color: theme.palette.neutralGray30.main,
                        },
                    }}
                />
            );
        };

        const checkedIcon = (
            <Box
                sx={{
                    position: "relative",
                    width: "20px",
                    height: "20px",
                }}
            >
                <BaseIcon color={theme.palette.deepBlue.main} disabledColor={theme.palette.neutralGray30.main} />
                <CheckmarkIcon
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        color: theme.palette.white.main,
                    }}
                />
            </Box>
        );

        const intermediateIcon = (
            <Box
                sx={{
                    position: "relative",
                    width: "20px",
                    height: "20px",
                }}
            >
                <BaseIcon color={theme.palette.deepBlue.main} />
                <MinusIcon
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        color: theme.palette.white.main,
                    }}
                />
            </Box>
        );

        return (
            <Checkbox
                ref={ref}
                data-testid={dataTestId}
                disableRipple
                icon={<BaseIcon />}
                checkedIcon={checkedIcon}
                indeterminateIcon={intermediateIcon}
                sx={{
                    color: hasError ? theme.palette.red.main : theme.palette.neutralGray30.main,
                    "&:not(.Mui-checked):hover": {
                        backgroundColor: "transparent",
                    },
                }}
                {...rest}
            />
        );
    },
);
